import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLandingContent } from "@/lib/landing/LandingContentProvider";
import { isLongLabel, phoneHref } from "@/lib/landing/resolve";
export default function LandingStickyBar({
  step,
  onBook,
  onCall,
}: {
  step: 1 | 2;
  onBook: () => void;
  onCall: () => void;
}) {
  const { content } = useLandingContent();
  const [bookVisible, setBookVisible] = useState(false);
  const [fieldFocused, setFieldFocused] = useState(false);
  useEffect(() => {
    const update = () =>
      setFieldFocused(
        Boolean(
          document.activeElement?.matches(
            "#book form input, #book form select, #book form textarea",
          ),
        ),
      );
    const blur = () => queueMicrotask(update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", blur);
    return () => {
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", blur);
    };
  }, []);
  useEffect(() => {
    const card = document.getElementById("book");
    if (!card) return;
    const observer = new IntersectionObserver(
      ([entry]) => setBookVisible(!entry.isIntersecting),
      { threshold: 0.05 },
    );
    observer.observe(card);
    return () => {
      observer.disconnect();
    };
  }, []);
  if (step === 2 || fieldFocused) return null;
  return (
    <div
      role="region"
      aria-label="Call or book"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-landing-border bg-landing-band px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 md:hidden"
    >
      <div className="mx-auto flex max-w-lg gap-2">
        {bookVisible && (
          <Button
            onClick={onBook}
            className={`h-[52px] flex-1 rounded-xl bg-landing-red text-[16.5px] font-bold text-primary-foreground hover:bg-landing-red-hover${isLongLabel(content.cta.label) ? " min-w-0 whitespace-normal px-2 leading-[1.15]" : ""}`}
          >
            {content.cta.label}
          </Button>
        )}
        <Button
          asChild
          variant="outline"
          className="h-[52px] flex-1 rounded-xl border-[1.5px] border-landing-on-dark bg-transparent text-[16.5px] font-bold text-landing-on-dark hover:bg-landing-panel"
        >
          <a href={phoneHref(content.phone)} onClick={onCall}>
            <Phone className="mr-2 h-5 w-5" />
            {bookVisible ? "Call" : `Call ${content.phone}`}
          </a>
        </Button>
      </div>
    </div>
  );
}
